import * as React from 'react';

import { Col, Tab } from '@rematter/ui-web';

import { useSettingsLocation } from '../../_state/settingsModule.state';
import { SettingsSubmodule } from '../_layouts/SettingsSubmodule';

import { FacilitiesTabView } from './_FacilitiesTabView';
import { useTeamManagementPermissions } from './_hooks/useTeamManagementPermissions';
import { RolesTabView } from './_RolesTabView';
import { UsersTabView } from './_UsersTabView';

import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import type { SettingsPath } from '~/modules/settings/routes';

export const TeamPermissionsRootView = () => {
  const t = useTranslations();
  const [location, setLocation] = useSettingsLocation();

  const permissions = useTeamManagementPermissions();

  const isFacilitiesTab =
    location === '/team-and-permissions' ||
    location === '/team-and-permissions/facilities';
  const isRolesTab = location === '/team-and-permissions/roles';
  const isUsersTab = location === '/team-and-permissions/users';

  const firstAllowedTab = React.useMemo((): SettingsPath | null => {
    if (permissions.canReadFacilities)
      return '/team-and-permissions/facilities';
    if (permissions.canReadRoles) return '/team-and-permissions/roles';
    if (permissions.canReadUsers) return '/team-and-permissions/users';
    return null;
  }, [
    permissions.canReadFacilities,
    permissions.canReadRoles,
    permissions.canReadUsers,
  ]);

  React.useEffect(() => {
    if (permissions.isLoading) return;

    if (isFacilitiesTab && !permissions.canReadFacilities && firstAllowedTab) {
      setLocation(firstAllowedTab);
    } else if (isRolesTab && !permissions.canReadRoles && firstAllowedTab) {
      setLocation(firstAllowedTab);
    } else if (isUsersTab && !permissions.canReadUsers && firstAllowedTab) {
      setLocation(firstAllowedTab);
    }
  }, [
    permissions.isLoading,
    permissions.canReadFacilities,
    permissions.canReadRoles,
    permissions.canReadUsers,
    isFacilitiesTab,
    isRolesTab,
    isUsersTab,
    firstAllowedTab,
    setLocation,
  ]);

  if (!permissions.isLoading && !permissions.hasAnyReadAccess) {
    return (
      <Col className="@TeamPermissionsRootView size-full items-center justify-center gap-2">
        <span className="text-lg font-medium">
          {t.teamAndPermissions.title}
        </span>
        <span className="text-sm text-neutral/60">
          {t.teamAndPermissions.noPermission}
        </span>
      </Col>
    );
  }

  return (
    <Col className="@TeamPermissionsRootView h-full">
      <SettingsSubmodule
        title={t.teamAndPermissions.title}
        subtitle={t.teamAndPermissions.subtitle}
        tabs={
          <>
            {permissions.canReadFacilities && (
              <Tab
                selected={isFacilitiesTab}
                onClick={() => setLocation('/team-and-permissions/facilities')}
              >
                {t.teamAndPermissions.tabs.facilities}
              </Tab>
            )}
            {permissions.canReadRoles && (
              <Tab
                selected={isRolesTab}
                onClick={() => setLocation('/team-and-permissions/roles')}
              >
                {t.teamAndPermissions.tabs.roles}
              </Tab>
            )}
            {permissions.canReadUsers && (
              <Tab
                selected={isUsersTab}
                onClick={() => setLocation('/team-and-permissions/users')}
              >
                {t.teamAndPermissions.tabs.users}
              </Tab>
            )}
          </>
        }
      >
        {isFacilitiesTab && permissions.canReadFacilities && (
          <FacilitiesTabView />
        )}
        {isRolesTab && permissions.canReadRoles && <RolesTabView />}
        {isUsersTab && permissions.canReadUsers && <UsersTabView />}
      </SettingsSubmodule>
    </Col>
  );
};
